/**
 * Frontend Analytics utility
 * Provides a unified interface for client-side analytics (Mixpanel, OpenPanel, etc.)
 */

import { useOpenPanel } from "@openpanel/nextjs";
import mixpanel from "mixpanel-browser";
import analyticsConfig, { userIdentificationProviders } from "./analyticsConfig";

// Simple mixpanel initialization
try {
  const shouldMixpanelTrackPageViews = analyticsConfig.pageViewProviders.includes("mixpanel");

  mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || "88d3dac90690db0293a6e1acec5ca152", {
    debug: process.env.NODE_ENV === "development",
    track_pageview: shouldMixpanelTrackPageViews,
    persistence: "localStorage",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("Mixpanel analytics initialized successfully");
  }
} catch (error) {
  console.error(`Failed to initialize Mixpanel: ${error.message}`);
  mixpanel = {
    track: () => {},
    identify: () => {},
    people: { set: () => {} },
  };
}

/**
 * Track an event in analytics platforms
 * @param {string} eventName - Name of the event to track
 * @param {Object} properties - Properties/data associated with the event
 */
const trackEvent = (eventName, properties = {}) => {
  if (!eventName) {
    console.warn("Analytics: Missing event name in trackEvent call");
    return;
  }

  const providers = analyticsConfig.defaultProviders;

  if (providers.includes("mixpanel")) {
    try {
      mixpanel.track(eventName, properties);
    } catch (error) {
      console.error(`Mixpanel ERROR for ${eventName}: ${error.message}`);
    }
  }

  // Track in OpenPanel if configured
  if (providers.includes("openpanel")) {
    const op = useOpenPanel();
    try {
      op.track(eventName, properties);
    } catch (error) {
      console.error(`OpenPanel ERROR for ${eventName}: ${error.message}`);
    }
  } else if (providers.includes("openpanel") && !op) {
    console.warn(`OpenPanel client not available to track "${eventName}" (but was configured)`);
  }
};

/**
 * Identify a user in analytics platforms
 * @param {string} userId - Unique identifier for the user
 * @param {Object} traits - User traits/properties
 */
const identifyUser = (userId, traits = {}) => {
  if (!userId) {
    console.warn("Analytics: Missing userId in identifyUser call");
    return;
  }

  const providers = analyticsConfig.userIdentificationProviders;

  if (providers.includes("mixpanel")) {
    try {
      mixpanel.identify(userId);
      if (Object.keys(traits).length > 0) {
        mixpanel.people.set(traits);
      }
    } catch (error) {
      console.error(`Failed to identify user "${userId}" in Mixpanel:`, error);
    }
  }

  // Identify in OpenPanel if configured
  if (providers.includes("openpanel")) {
    const op = useOpenPanel();
    try {
      op.identify({
        profileId: userId,
        ...traits,
      });
    } catch (error) {
      console.error(`Failed to identify user "${userId}" in OpenPanel:`, error);
    }
  } else if (providers.includes("openpanel")) {
    console.warn(
      `OpenPanel client not available to identify user "${userId}" (but was configured)`,
    );
  }
};

/**
 * Check if Mixpanel analytics is properly initialized
 * @returns {boolean} Whether Mixpanel is initialized
 */
const isMixpanelAvailable = () => !!mixpanel && typeof mixpanel.track === "function";

module.exports = {
  trackEvent,
  identifyUser,
  isMixpanelAvailable,
};
